import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauBuoc from "components/DauBuoc";
import { NhanDau } from "components/DauTrang";
import { vuonApi } from "api/vuon";
import { chuVuonApi } from "api/chuVuon";
import { chiFieldThayDoi, yeuCauSuaChuVuonVuonApi } from "api/yeuCauSuaChuVuonVuon";
import FormVuon, { RONG_VUON } from "components/FormVuon";
import { kiemTraThongTinVuon } from "lib/thongTinVuon";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
  oDialogFormFlex,
} from "lib/dungDialogFullManHinh";
import { chuXuongDong } from "lib/kieuBangDuLieu";

function ChonVuon() {
  const { maChuVuon } = useParams();
  const navigate = useNavigate();
  const fullManHinh = useDialogFullManHinh();
  const [chuVuon, setChuVuon] = useState(null);
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [taoMoi, setTaoMoi] = useState(RONG_VUON);
  const [diaChiVuonGiongChu, setDiaChiVuonGiongChu] = useState(true);
  const [dangTaoMoi, setDangTaoMoi] = useState(false);
  const [dangLuu, setDangLuu] = useState(false);
  const [loi, setLoi] = useState("");
  const [vuonGoc, setVuonGoc] = useState(null);
  const [modalDeXuat, setModalDeXuat] = useState(null);
  const [dangGuiDeXuat, setDangGuiDeXuat] = useState(false);
  const [loiDeXuat, setLoiDeXuat] = useState("");
  const [thongBaoDeXuat, setThongBaoDeXuat] = useState("");

  useEffect(() => {
    Promise.all([vuonApi.theoChuVuon(maChuVuon), chuVuonApi.chiTiet(maChuVuon).catch(() => null)])
      .then(([ds, cv]) => {
        setDanhSach(ds);
        setChuVuon(cv);
      })
      .catch((err) => setLoi(err.message))
      .finally(() => setDangTai(false));
  }, [maChuVuon]);

  function moFormTaoMoi() {
    setDangTaoMoi(true);
    setDiaChiVuonGiongChu(true);
    setLoi("");
    setTaoMoi({
      ...RONG_VUON,
      tinh: chuVuon?.tinh ?? "",
      xa: chuVuon?.xa ?? "",
      thon: chuVuon?.thon ?? "",
      diaChiChiTiet: chuVuon?.diaChi ?? "",
    });
  }

  function diThuMua(maVuon) {
    navigate(`/thu-mua/${maChuVuon}/${maVuon}/nhap`);
  }

  async function xacNhanTaoMoi(e) {
    e.preventDefault();
    setLoi("");
    setDangLuu(true);
    try {
      const daKiem = kiemTraThongTinVuon({ ...taoMoi, maChuVuon });
      const vuon = await vuonApi.taoMoi(daKiem);
      diThuMua(vuon.id);
    } catch (err) {
      setLoi(err.message);
      setDangLuu(false);
    }
  }

  function moDeXuatSuaVuon(vuon) {
    setLoiDeXuat("");
    setThongBaoDeXuat("");
    setVuonGoc(vuon);
    setModalDeXuat({ ...vuon });
  }

  async function guiDeXuatVuon(e) {
    e.preventDefault();
    setDangGuiDeXuat(true);
    setLoiDeXuat("");
    try {
      const daKiem = kiemTraThongTinVuon({ ...modalDeXuat, maChuVuon });
      const chuanHoa = {
        ...daKiem,
        dienTichHa: daKiem.dienTichHa,
        namBatDauTrong: modalDeXuat.namBatDauTrong ? Number(modalDeXuat.namBatDauTrong) : null,
        soCay: modalDeXuat.soCay ? Number(modalDeXuat.soCay) : null,
      };
      const patch = chiFieldThayDoi(vuonGoc, chuanHoa);
      if (Object.keys(patch).length === 0) {
        throw new Error("Không có thay đổi nào để gửi duyệt.");
      }
      await yeuCauSuaChuVuonVuonApi.guiYeuCau({
        loai: "vuon",
        targetId: vuonGoc.id,
        duLieuMoi: patch,
      });
      setModalDeXuat(null);
      setVuonGoc(null);
      setThongBaoDeXuat("Đã gửi yêu cầu sửa vườn — admin duyệt tại Chờ duyệt CV/Vườn.");
    } catch (err) {
      setLoiDeXuat(err.message);
    } finally {
      setDangGuiDeXuat(false);
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card sx={{ mb: 3 }}>
              <MDBox p={{ xs: 2.5, sm: 3 }}>
                <DauBuoc
                  buoc={2}
                  tieuDe="Chọn vườn"
                  moTa="Chọn vườn đang thu hoạch, hoặc thêm vườn mới cho chủ vườn này."
                  ngoaiCanh={chuVuon?.hoTen}
                />

                {!dangTaoMoi && loi && (
                  <MDBox mb={2}>
                    <MDAlert color="error">{loi}</MDAlert>
                  </MDBox>
                )}

                {thongBaoDeXuat && (
                  <MDBox mb={2}>
                    <MDAlert color="success">
                      {thongBaoDeXuat}{" "}
                      <MDTypography
                        component={Link}
                        to="/cho-duyet/chu-vuon-vuon"
                        variant="caption"
                        color="success"
                        sx={{ textDecoration: "underline" }}
                      >
                        Xem yêu cầu của tôi
                      </MDTypography>
                    </MDAlert>
                  </MDBox>
                )}

                {dangTai ? (
                  <MDTypography variant="button" color="text">
                    Đang tải...
                  </MDTypography>
                ) : danhSach.length === 0 ? (
                  <MDBox py={2} textAlign="center">
                    <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                      grass
                    </Icon>
                    <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                      Chủ vườn này chưa có vườn nào.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      Thêm vườn mới bên dưới để tiếp tục thu mua.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    <MDTypography variant="overline" color="text">
                      Chọn vườn thu hoạch
                    </MDTypography>
                    <List sx={{ mt: 0.5, py: 0 }}>
                      {danhSach.map((v, i) => (
                        <MDBox key={v.id}>
                          <MDBox display="flex" alignItems="stretch" gap={0.5}>
                            <ListItemButton
                              onClick={() => diThuMua(v.id)}
                              sx={({ palette }) => ({
                                flex: 1,
                                borderRadius: "0.5rem",
                                px: 1.5,
                                py: 1.25,
                                gap: 1,
                                "&:hover": { backgroundColor: palette.grey[100] },
                              })}
                            >
                              <MDBox flex={1} minWidth={0}>
                                <MDTypography variant="button" fontWeight="medium" display="block">
                                  {v.tenVuon}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  {v.xa}, {v.tinh}
                                </MDTypography>
                              </MDBox>
                              <Icon sx={({ palette }) => ({ color: palette.grey[500] })}>
                                chevron_right
                              </Icon>
                            </ListItemButton>
                            <IconButton
                              color="info"
                              title="Đề xuất sửa vườn"
                              onClick={() => moDeXuatSuaVuon(v)}
                              sx={({ palette }) => ({
                                alignSelf: "center",
                                border: `1px solid ${palette.grey[300]}`,
                                borderRadius: "0.5rem",
                              })}
                            >
                              <Icon fontSize="small">edit_note</Icon>
                            </IconButton>
                          </MDBox>
                          {i < danhSach.length - 1 && <Divider sx={{ my: 0.25 }} />}
                        </MDBox>
                      ))}
                    </List>
                  </MDBox>
                )}

                {!dangTaoMoi && (
                  <MDBox
                    mt={2.5}
                    pt={2}
                    sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}` })}
                  >
                    <MDButton fullWidth variant="outlined" color="info" onClick={moFormTaoMoi}>
                      <Icon sx={{ mr: 0.75 }}>add_location_alt</Icon>
                      Thêm vườn mới
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            </Card>

            {dangTaoMoi && (
              <Card>
                <MDBox p={{ xs: 2.5, sm: 3 }} component="form" onSubmit={xacNhanTaoMoi}>
                  <MDTypography variant="overline" color="text">
                    Vườn chưa có trong hệ thống
                  </MDTypography>
                  <MDTypography variant="h5" mb={0.5}>
                    Thêm vườn mới
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    display="block"
                    mb={2}
                  >
                    Chọn ít nhất một giống bơ đang trồng để tiếp tục.
                  </MDTypography>
                  {loi && (
                    <MDBox mb={2}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}
                  <Grid container spacing={2}>
                    <FormVuon
                      value={taoMoi}
                      onChange={setTaoMoi}
                      chuVuon={chuVuon}
                      giongChuVuon={diaChiVuonGiongChu}
                      onChangeGiongChuVuon={setDiaChiVuonGiongChu}
                    />
                  </Grid>
                  <MDBox
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    mt={3}
                    sx={{ "& > *": { width: { xs: "100%", sm: "auto" } } }}
                  >
                    <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
                      {dangLuu ? "Đang tạo..." : "Tạo vườn"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => setDangTaoMoi(false)}
                    >
                      Huỷ
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={!!modalDeXuat}
        onClose={() => setModalDeXuat(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>Đề xuất sửa</NhanDau>
          <MDTypography variant="h5" component="h2">
            Vườn
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Thay đổi sẽ gửi admin duyệt — không cập nhật trực tiếp hồ sơ.
          </MDTypography>
        </DialogTitle>
        {modalDeXuat && (
          <MDBox component="form" onSubmit={guiDeXuatVuon} sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              {loiDeXuat && (
                <MDBox mb={2}>
                  <MDAlert color="error">{loiDeXuat}</MDAlert>
                </MDBox>
              )}
              <Grid container spacing={2}>
                <FormVuon
                  value={modalDeXuat}
                  onChange={setModalDeXuat}
                  chuVuon={chuVuon}
                  giongChuVuon={false}
                  onChangeGiongChuVuon={() => {}}
                />
              </Grid>
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModalDeXuat(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangGuiDeXuat}>
                {dangGuiDeXuat ? "Đang gửi..." : "Gửi duyệt"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default ChonVuon;
